// Button component for UI Component Library
// Created as part of ENG2-1691: Phase 3 - Ticket 3.1

import React from 'react'
import { Button as MuiButton, CircularProgress, Box } from '@mui/material'
import { styled, CSSObject } from '@mui/material/styles'
import { tokens } from '../../../styles/tokens'
import { ButtonProps, ButtonVariant, ButtonSize } from './button.types'

// Style configurations for each variant
const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    backgroundColor: tokens.colors.primary[600],
    color: tokens.colors.white,
    border: 'none',
  },
  secondary: {
    backgroundColor: tokens.colors.white,
    color: tokens.colors.grey[700],
    border: `1px solid ${tokens.colors.grey[300]}`,
  },
  ghost: {
    backgroundColor: 'transparent',
    color: tokens.colors.grey[600],
    border: 'none',
  },
  danger: {
    backgroundColor: tokens.colors.error[500],
    color: tokens.colors.white,
    border: 'none',
  },
}

// Hover styles for each variant
const hoverStyles: Record<ButtonVariant, React.CSSProperties> = {
  primary: {
    backgroundColor: tokens.colors.primary[700],
  },
  secondary: {
    backgroundColor: tokens.colors.grey[50],
    borderColor: tokens.colors.grey[400],
  },
  ghost: {
    backgroundColor: tokens.colors.grey[100],
  },
  danger: {
    backgroundColor: tokens.colors.error[600],
  },
}

// Size configurations
const sizeStyles: Record<ButtonSize, { height: number; fontSize: string; padding: string }> = {
  medium: {
    height: 38,
    fontSize: '13px',
    padding: '8px 16px',
  },
  small: {
    height: 32,
    fontSize: '12px',
    padding: '6px 12px',
  },
}

// Styled button component
const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) =>
    !['buttonVariant', 'buttonSize', 'loading'].includes(prop as string),
})<{ buttonVariant: ButtonVariant; buttonSize: ButtonSize; loading?: boolean }>(
  ({ buttonVariant, buttonSize, loading }) =>
    ({
      ...variantStyles[buttonVariant],
      ...sizeStyles[buttonSize],
      borderRadius: tokens.radius.sm,
      fontWeight: 600,
      textTransform: 'none',
      boxShadow: 'none',
      transition: `all ${tokens.transitions.fast}`,
      position: 'relative',
      cursor: loading ? 'wait' : 'pointer',

      '&:hover': {
        ...hoverStyles[buttonVariant],
        boxShadow: 'none',
      },

      '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
      },

      '& .MuiButton-startIcon': {
        marginRight: 8,
      },

      '& .MuiButton-endIcon': {
        marginLeft: 8,
      },
    } as CSSObject)
)

// Loading spinner wrapper
const LoadingWrapper = styled(Box)({
  position: 'absolute',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

// Content wrapper that fades when loading
const ContentWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'loading',
})<{ loading?: boolean }>(({ loading }) => ({
  display: 'flex',
  alignItems: 'center',
  opacity: loading ? 0 : 1,
  transition: 'opacity 150ms ease',
}))

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      variant = 'primary',
      size = 'medium',
      startIcon,
      endIcon,
      loading = false,
      disabled = false,
      fullWidth = false,
      onClick,
      children,
      sx,
      type = 'button',
      className,
      'data-cy': dataCy,
      ...other
    } = props

    return (
      <StyledButton
        ref={ref}
        buttonVariant={variant}
        buttonSize={size}
        loading={loading}
        disabled={disabled || loading}
        fullWidth={fullWidth}
        onClick={onClick}
        type={type}
        className={className}
        data-cy={dataCy}
        data-ui-button="true"
        sx={sx}
        {...other}
      >
        {loading && (
          <LoadingWrapper>
            <CircularProgress
              size={size === 'small' ? 16 : 20}
              sx={{
                color: variant === 'secondary' || variant === 'ghost'
                  ? tokens.colors.grey[600]
                  : tokens.colors.white,
              }}
            />
          </LoadingWrapper>
        )}
        <ContentWrapper loading={loading}>
          {startIcon && <Box component="span" sx={{ display: 'flex', mr: 1 }}>{startIcon}</Box>}
          {children}
          {endIcon && <Box component="span" sx={{ display: 'flex', ml: 1 }}>{endIcon}</Box>}
        </ContentWrapper>
      </StyledButton>
    )
  }
)

Button.displayName = 'UIButton'

export default Button
export type { ButtonProps, ButtonVariant, ButtonSize } from './button.types'
